<template>
    <div class="w:full xl:w-3/4">
        
        <div class="flex gap-x-4 justify-between items-end">
            <!-- Substitutions -->
            <div v-if="order.allow_substitutions" class="pb-1">
                <span class="font-bold">Substitutions Allowed</span>
                <div class="text-xs">If an item is unavailable when preparing your order, we may substitute some or all of it for a similar item.</div>
            </div>
            <div v-else class="pb-1">
                <span class="font-bold">Substitutions Not Allowed</span>
                <div class="text-xs">If an item is unavailable when preparing your order, we will only give what's available without substituting for a similar item.</div>
            </div>

            <div v-if="!isCustomer && (order.checker_id || order.picker_id)" class="text-right">
                <!-- Checked By -->
                <div v-if="!isCustomer && order.checker_id" class="font-bold">
                    Checked by {{ order.checker.name }}
                    <div v-if="order.picking_checked_at" class="text-xs">{{ formatTimestamp(order.picking_checked_at) }}</div>
                </div>

                <!-- Picked By -->
                <div v-if="!isCustomer && order.picker_id" class="font-bold">
                    Picked by {{ order.picker.name }}
                </div>
            </div>
        </div>

        <div class="rounded-t overflow-hidden">
            <table class="w-full">
                <thead>
                    <tr class="h-10 align-bottom bg-gray-light border">
                        <th class="px-2 py-1 text-center">BC</th>
                        <th v-if="!isCustomer" class="px-2 py-1 text-left whitespace-nowrap">Item #</th>
                        <th class="px-2 py-1 text-left md:w-1/3">Product</th>
                        <th class="hidden md:table-cell px-2 py-1 text-left w-1/3">Notes</th>
                        <th class="hidden sm:table-cell px-2 py-1 text-center">Price</th>
                        <th class="hidden sm:table-cell px-2 py-1 text-center">Quantity</th>
                        <th class="sm:hidden px-2 py-1 text-center whitespace-nowrap">Qty @ Price</th>
                        <th v-if="showPromoFinancials" class="px-2 py-1 text-right whitespace-nowrap">Discount</th>
                        <th class="px-2 py-1 text-right whitespace-nowrap">Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="item in order.order_items" :key="item.id"
                        class="border striped"
                        :class="{
                            'item_picked': !isCustomer && !hasShortage(item) && item.checking_status == 'picked',
                            'item_shorted': !isCustomer && hasShortage(item),
                        }"
                    >
                        <td class="px-2 py-1">
                            <div>
                                <img src="/images/icons/green_check.svg" v-if="BCInfoFilled(item) == 'no_error'" width="24" height="24" class="mx-auto" />
                                <img src="/images/icons/x-red.svg" v-if="BCInfoFilled(item) == 'error'" width="24" height="24" class="mx-auto" :title="errorMessageBC(item.bc_error)" />
                            </div>
                        </td>
                        <td v-if="!isCustomer" class="px-2 py-1">
                            {{ item.product_id }}
                        </td>
                        <td class="px-2 py-1">
                            <div>
                                {{ item.title }}
                                <span v-if="item.description"> - {{ item.description }}</span>
                                <span v-if="item.size"> - {{ item.size }}</span>
                                <span v-if="item.product.special_order" class="ml-1 text-xs italic text-red">(Special Order Item)</span>
                            </div>

                            <div v-if="!isCustomer && item.is_shorted" class="text-xs text-red-700 mt-1">
                                Short: ordered {{ formatQuantity(item.orig_quantity) }}, fulfilled {{ formatQuantity(item.quantity) }}.
                            </div>

                            <div
                                v-for="subitem in shortedSubitems(item)"
                                :key="subitem?.id"
                                v-if="!isCustomer && subitem"
                                class="text-xs text-red-700 mt-1"
                            >
                                Component short: {{ subitem?.title }}<span v-if="subitem?.description"> - {{ subitem.description }}</span><span v-if="subitem?.size"> - {{ subitem.size }}</span> ordered {{ formatQuantity(subitem?.orig_quantity) }}, fulfilled {{ formatQuantity(subitem?.quantity) }}.
                            </div>

                            <div v-if="item.recurring_order_item_id && item.recurring_order_item?.recurring_order_id" class="text-xs text-gray-500 mt-1">
                                Item generated by
                                <Link
                                    :href="route('recurring_orders.show', item.recurring_order_item.recurring_order_id)"
                                    class="text-blue-600 hover:underline"
                                >
                                    recurring order #{{ item.recurring_order_item.recurring_order_id }}
                                </Link>.
                            </div>
                        </td>
                        <td class="hidden md:table-cell px-2 py-1">
                            {{ item.product_notes }}
                            <div v-if="!isCustomer && item.picker_id" class="text-xs italic">
                                Picked by <b>{{ item.picker.name }}</b>
                                <span v-if="item.picked_at"> at {{ formatTimestamp(item.picked_at) }}</span>
                            </div>
                            <div v-if="!isCustomer && item.checker_id" class="text-xs italic">
                                Checked by <b>{{ item.checker.name }}</b>
                                <span v-if="item.checked_at"> at {{ formatTimestamp(item.checked_at) }}</span>
                            </div>
                            <div v-if="!isCustomer && item.is_shorted && item.auth_code" class="text-xs italic">
                                Short authorized by <b>{{ item.auth_code.name }}</b>.
                            </div>
                            <div
                                v-for="subitem in shortedSubitems(item)"
                                :key="`auth-${subitem?.id}`"
                                v-if="!isCustomer && subitem?.auth_code_name"
                                class="text-xs italic"
                            >
                                {{ subitem?.title }} short authorized by <b>{{ subitem?.auth_code_name }}</b>.
                            </div>
                        </td>
                        <td class="hidden sm:table-cell px-2 py-1 text-center">{{ toCurrency(item.price) }}</td>
                        <td class="hidden sm:table-cell px-2 py-1 text-center">{{ item.quantity }}</td>
                        <td class="sm:hidden px-2 py-1 text-center">{{ item.quantity }} @ ${{item.price}}</td>
                        <td v-if="showPromoFinancials" class="px-2 py-1 text-right whitespace-nowrap">{{ lineDiscountAmt(item) > 0 ? toCurrency(lineDiscountAmt(item)) : '' }}</td>
                        <td class="px-2 py-1 text-right whitespace-nowrap">{{ toCurrency(lineTotalDisplay(item)) }}</td>
                    </tr>
                    <tr v-if="promoLocked">
                        <td colspan="1"></td>
                        <td v-if="!isCustomer" colspan="1"></td>
                        <td colspan="1" class="hidden sm:table-cell"></td>
                        <td colspan="1" class="hidden md:table-cell"></td>
                        <td colspan="2" class="text-right font-bold px-2 pt-2 pb-1">Subtotal:</td>
                        <td class="text-right px-2 pt-2 pb-1">{{ toCurrency(subtotalBeforeDiscount) }}</td>
                    </tr>
                    <template v-if="!promoLocked">
                        <tr>
                            <td colspan="1"></td>
                            <td v-if="!isCustomer" colspan="1"></td>
                            <td colspan="1" class="hidden sm:table-cell"></td>
                            <td colspan="1" class="hidden md:table-cell"></td>
                            <td :colspan="hasPromo ? 3 : 2" class="text-right font-bold px-2 pt-2 pb-1">Subtotal:</td>
                            <td class="text-right px-2 pt-2 pb-1">{{ toCurrency(subtotal) }}</td>
                        </tr>
                        <tr>
                            <td colspan="1"></td>
                            <td v-if="!isCustomer" colspan="1"></td>
                            <td colspan="1" class="hidden sm:table-cell"></td>
                            <td colspan="1" class="hidden md:table-cell"></td>
                            <td :colspan="hasPromo ? 3 : 2" class="text-right font-bold px-2 pt-2 pb-1">Delivery Fee:</td>
                            <td class="text-right px-2 pt-2 pb-1">{{ toCurrency(delivery_fee) }}</td>
                        </tr>
                        <tr v-if="!isSelectedCustomerTaxExempt">
                            <td colspan="1"></td>
                            <td v-if="!isCustomer" colspan="1"></td>
                            <td colspan="1" class="hidden sm:table-cell"></td>
                            <td colspan="1" class="hidden md:table-cell"></td>
                            <td :colspan="hasPromo ? 3 : 2" class="px-2 py-1 text-right font-bold">Tax:</td>
                            <td class="px-2 py-1 text-right">{{ toCurrency(tax) }}</td>
                        </tr>
                        <tr v-if="order.applied_credit_amount > 0">
                            <td colspan="1"></td>
                            <td v-if="!isCustomer" colspan="1"></td>
                            <td colspan="1" class="hidden sm:table-cell"></td>
                            <td colspan="1" class="hidden md:table-cell"></td>
                            <td :colspan="hasPromo ? 3 : 2" class="px-2 py-1 text-right font-bold">Prepaid:</td>
                            <td class="px-2 py-1 text-right">{{ toCurrency(parseFloat(order.applied_credit_amount)) }}</td>
                        </tr>
                        <tr>
                            <td colspan="1"></td>
                            <td v-if="!isCustomer" colspan="1"></td>
                            <td colspan="1" class="hidden sm:table-cell"></td>
                            <td colspan="1" class="hidden md:table-cell"></td>
                            <td :colspan="hasPromo ? 3 : 2" class="px-2 py-1 text-right font-bold">Total:</td>
                            <td class="px-2 py-1 text-right">{{ toCurrency(total) }}</td>
                        </tr>
                    </template>
                    <tr v-if="order.exported_at === null && order.status !== 2 && !is_past_ship_date">
                        <td colspan="1"></td>
                        <td v-if="!isCustomer" colspan="1"></td>
                        <td colspan="1" class="hidden sm:table-cell"></td>
                        <td colspan="1" class="hidden md:table-cell"></td>
                        <td :colspan="showPromoFinancials ? 3 : 2" class="px-2 py-1 text-right font-bold">Lock in pricing:</td>
                        <td class="px-2 py-1 text-right">{{ order.is_pricing_locked ? 'Yes' : 'No' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-if="order.applied_promo_code" class="mt-3 p-3 rounded bg-gray-100 w-fit whitespace-nowrap float-right text-right">
            <template v-if="promoLocked">
                <jet-secondary-button type="button" @click="openPromoModal">
                    Show Discounts
                </jet-secondary-button>
            </template>
            <template v-else>
                <div class="font-bold">Promo Code: {{ order.applied_promo_code.code }}<span v-if="order.promo_code_percent"> ({{ order.promo_code_percent }}%)</span></div>
                <div class="text-gray-600">- {{ toCurrency(orderTotals.promoTotal) }} applied</div>
            </template>
        </div>

        <jet-dialog-modal :show="showPromoModal" @close="closePromoModal">
            <template #title>
                <h3 class="text-2xl">Enter Password</h3>
            </template>

            <template #content>
                <div class="my-4">
                    <jet-label value="Password:" class="mb-2" />
                    <jet-input
                        v-model="promoPassword"
                        type="password"
                        class="w-full"
                        ref="promoPasswordInput"
                        placeholder="Enter password to view promo code"
                        @keyup.enter="submitPromoPassword"
                    />
                    <jet-input-error :message="promoError" class="mt-1" />
                </div>
            </template>

            <template #footer>
                <jet-button type="button" @click="submitPromoPassword" :disabled="promoPasswordSubmitting">
                    Verify
                </jet-button>
                <jet-tertiary-button type="button" @click="closePromoModal" class="ml-2">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>
    </div>
</template>

<script>
import { usePage, Link } from '@inertiajs/inertia-vue3'
import axios from 'axios';
import UseUserAuth from '@/Composables/UseUserAuth';
import { shouldApplySplitCaseSurchargeForItem } from '@/Composables/UseProductQuantity';
import { computeOrderTotals, getLineDiscountedTotal as getLineDiscountedTotalFn, lineDiscountAmount } from '@/Composables/useOrderTotals';
import { DateTime } from "luxon";
import JetDialogModal from '@/Jetstream/DialogModal';
import JetButton from '@/Jetstream/Button';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';

export default {

    props: {
        order: { type: Object, required: true },
    },

    components: {
        Link,
        JetDialogModal,
        JetButton,
        JetSecondaryButton,
        JetTertiaryButton,
        JetInput,
        JetInputError,
        JetLabel,
    },

    setup () {
        const { isAdmin, isCustomer } = UseUserAuth(usePage().props.value.user);

        return {
            isAdmin,
            isCustomer,
        }
    },

    data () {
        return {
            promoRevealed: false,
            showPromoModal: false,
            promoPassword: '',
            promoError: '',
            promoPasswordSubmitting: false,
        }
    },

    computed: {
        orderTotals () {
            return computeOrderTotals(this.order);
        },
        hasPromo () {
            return !!(this.order.applied_promo_code || this.order.promo_code_id);
        },
        promoLocked () {
            return !this.isCustomer && this.hasPromo && !this.promoRevealed;
        },
        showPromoFinancials () {
            return this.hasPromo && !this.promoLocked;
        },
        isSelectedCustomerTaxExempt () {
            if (this.order.tax_exempt === 1) return true;
            if (this.order.tax_exempt === null && this.order.customer && this.order.customer.tax_exempt) return true;
            return false;
        },
        subtotal () {
            return this.orderTotals.subtotal;
        },
        subtotalBeforeDiscount () {
            return this.orderTotals.subtotalBeforeDiscount;
        },
        tax () {
            return this.orderTotals.tax;
        },
        total () {
            return this.orderTotals.total;
        },
        delivery_fee () {
            return this.orderTotals.deliveryFeeAfterDiscount;
        },
        is_past_ship_date () {
            return new Date(this.order.ship_date) < new Date();
        }
    },

    methods: {
        openPromoModal () {
            this.promoError = '';
            this.promoPassword = '';
            this.showPromoModal = true;
            this.$nextTick(() => this.$refs.promoPasswordInput?.focus());
        },

        closePromoModal () {
            this.showPromoModal = false;
            this.promoPassword = '';
            this.promoError = '';
        },

        submitPromoPassword () {
            this.promoError = '';
            this.promoPasswordSubmitting = true;

            axios.post(route('orders.promo_code.verify_password'), { password: this.promoPassword })
                .then(() => {
                    this.promoRevealed = true;
                    this.closePromoModal();
                })
                .catch(() => {
                    this.promoError = 'Invalid password';
                })
                .finally(() => {
                    this.promoPasswordSubmitting = false;
                });
        },

        hasShortage(item) {
            return Boolean(item?.has_shortage);
        },
        shortedSubitems(item) {
            const raw = Array.isArray(item?.shorted_subitems) ? item.shorted_subitems : [];
            return raw.filter((sub) => sub != null && typeof sub === 'object');
        },
        formatQuantity(quantity) {
            const parsedQuantity = Number(quantity);

            return Number.isFinite(parsedQuantity) ? parsedQuantity.toString() : quantity;
        },
        isSplitCaseQuantity(quantity) {
            const parsedQuantity = Number(quantity);
            return parsedQuantity > 0 && parsedQuantity < 1;
        },
        shouldApplySplitCaseSurcharge(quantity, item) {
            return shouldApplySplitCaseSurchargeForItem(quantity, this.order?.customer, item);
        },
        splitCaseBasePrice(item) {
            if (item.product?.price != null && !shouldApplySplitCaseSurchargeForItem(item.quantity, this.order?.customer, item)) return item.product.price;
            if (shouldApplySplitCaseSurchargeForItem(item.quantity, this.order?.customer, item) && item.price != null) return Math.round((item.price / 1.1) * 100) / 100;
            return item.price;
        },
        BCInfoFilled (order) {
            if (order.bc_error != null)
                return 'error';
            else if (order.bc_id != null && order.bc_error == null)
                return 'no_error';
            else 
                return false;
        },

        lineDiscountAmt (item) {
            return lineDiscountAmount(this.order, item);
        },

        lineTotalDisplay (item) {
            if (this.promoLocked) {
                return Math.round((Number(item.price) || 0) * (Number(item.quantity) || 0) * 100) / 100;
            }

            return getLineDiscountedTotalFn(this.order, item);
        },

        getLineDiscountedTotal (order, item) {
            return getLineDiscountedTotalFn(order, item);
        },

        errorMessageBC(errorString) {
            try {
                // Check if the error string is XML
                if (errorString.startsWith('<error')) {
                    // Parse XML string
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(errorString, 'application/xml');
                    const code = xmlDoc.getElementsByTagName('code')[0]?.textContent;
                    const message = xmlDoc.getElementsByTagName('message')[0]?.textContent;
                    if (code && message) {
                        return `${code}. ${message}`;
                    }
                } else {
                    // Parse JSON string
                    const errorObj = JSON.parse(errorString);
                    if (errorObj.error && errorObj.error.message) {
                        return errorObj.error.message;
                    }
                }
            } catch (e) {
                console.error('Error parsing the error string:', e);
            }

            return 'An unknown error occurred';
        },

        formatTimestamp(timestamp) {
            return DateTime.fromSQL(timestamp).toFormat('MMM. d, yyyy h:mm a');
        },
    }
}
</script>

<style scoped>
    .item_picked {
        @apply bg-green bg-opacity-20 !important;
    }
    .item_shorted {
        @apply bg-yellow-400 bg-opacity-20 !important;
    }
</style>
